import { useEffect, useRef, useState } from 'react'
import { useUserContext } from '@/UserContext'
import { cn } from '@/utlis/cn'
import LeaveSessionMenuButton from '@/pages/SessionPage/internal/toolbar/LeaveSessionMenuButton'
import MusicDevicesMenuButton from '@/pages/SessionPage/internal/toolbar/MusicDevicesMenuButton'
import ReactionsMenuButton from '@/pages/SessionPage/internal/toolbar/ReactionsMenuButton'
import RecordingMenuButton from '@/pages/SessionPage/internal/toolbar/RecordingMenuButton'
import ShareScreenMenuButton from '@/pages/SessionPage/internal/toolbar/ShareScreenMenuButton'
import UserAudioMenuButton from '@/pages/SessionPage/internal/toolbar/UserAudioMenuButton'
import UserVideoMenuButton from '@/pages/SessionPage/internal/toolbar/UserVideoMenuButton'
import useChatRoomController from '@/pages/ChatRoom/useChatRoomController'

import './toolbar.css'
import { OverflowMenuButton } from './OverflowMenuButton'
import { FeatureFlags } from '@/services/FeatureFlags'
import { ChatMenuButton } from './ChatMenuButton'
import { env } from '@/services/EnvironmentVariables'
import CollaboratorsMenuButton from './CollaboratorsMenuButton'

const OverflowThresholds = {
    invite: 885,
    reactions: 885,
    overflow: 885,
    recording: 805,
    nogap: 805,
    musicDevices: 595,
    video: 370,
    screenshare: 305,
}

export function Toolbar({
    inSidebar,
    chatController,
}: {
    inSidebar?: boolean
    chatController?: ReturnType<typeof useChatRoomController>
}) {
    const [userState] = useUserContext()

    const [toolbarNode, setToolbarNode] = useState<HTMLDivElement>()
    const toolbarRef = (node: HTMLDivElement) => {
        setToolbarNode(node)
    }

    const [overflowScreenshare, setOverflowScreenshare] = useState(false)
    const [overflowReactions, setOverflowReactions] = useState(false)
    const [overflowMusicDevices, setOverflowMusicDevices] = useState(false)
    const [overflowRecording, setOverflowRecording] = useState(false)
    const [overflowInvite, setOverflowInvite] = useState(false)
    const [overflowVideo, setOverflowVideo] = useState(false)

    const [showOverflowButton, setShowOverflowButton] = useState(false)
    const [collapseSectionGap, setCollapseSectionGap] = useState(false)

    const lastWidth = useRef<number>(0)

    useEffect(() => {
        if (!toolbarNode) {
            return
        }
        lastWidth.current = toolbarNode.clientWidth

        function handleToolbarResize(toolbarNode: HTMLDivElement, force: boolean = false) {
            const doesOverflowScreenshare = lastWidth.current < OverflowThresholds.screenshare
            const shouldOverflowScrenshare = toolbarNode.clientWidth < OverflowThresholds.screenshare
            if (doesOverflowScreenshare !== shouldOverflowScrenshare || force) {
                setOverflowScreenshare(shouldOverflowScrenshare)
            }

            const doesOverflowReactions = lastWidth.current < OverflowThresholds.reactions
            const shouldOverflowReactions = toolbarNode.clientWidth < OverflowThresholds.reactions
            if (doesOverflowReactions !== shouldOverflowReactions || force) {
                setOverflowReactions(shouldOverflowReactions)
            }

            const doesOverflowMusicDevices = lastWidth.current < OverflowThresholds.musicDevices
            const shouldOverflowMusicDevices = toolbarNode.clientWidth < OverflowThresholds.musicDevices
            if (doesOverflowMusicDevices !== shouldOverflowMusicDevices || force) {
                setOverflowMusicDevices(shouldOverflowMusicDevices)
            }

            const doesOverflowRecording = lastWidth.current < OverflowThresholds.recording
            const shouldOverflowRecording = toolbarNode.clientWidth < OverflowThresholds.recording
            if (doesOverflowRecording !== shouldOverflowRecording || force) {
                setOverflowRecording(shouldOverflowRecording)
            }

            const doesShowOverflowButton = lastWidth.current < OverflowThresholds.overflow
            const shouldShowOverflowButton = toolbarNode.clientWidth < OverflowThresholds.overflow
            if (doesShowOverflowButton !== shouldShowOverflowButton || force) {
                setShowOverflowButton(shouldShowOverflowButton)
            }

            const doesCollapseSectionGap = lastWidth.current < OverflowThresholds.nogap
            const shouldCollapseSectionGap = toolbarNode.clientWidth < OverflowThresholds.nogap
            if (doesCollapseSectionGap !== shouldCollapseSectionGap || force) {
                setCollapseSectionGap(shouldCollapseSectionGap)
            }

            const doesOverflowInvite = lastWidth.current < OverflowThresholds.invite
            const shouldOverflowInvite = toolbarNode.clientWidth < OverflowThresholds.invite
            if (doesOverflowInvite !== shouldOverflowInvite || force) {
                setOverflowInvite(shouldOverflowInvite)
            }

            const doesOverflowVideo = lastWidth.current < OverflowThresholds.video
            const shouldOverflowVideo = toolbarNode.clientWidth < OverflowThresholds.video
            if (doesOverflowVideo !== shouldOverflowVideo || force) {
                setOverflowVideo(shouldOverflowVideo)
            }

            lastWidth.current = toolbarNode.clientWidth
        }

        handleToolbarResize(toolbarNode, true)

        const resizeObserver = new ResizeObserver(() => handleToolbarResize(toolbarNode))
        resizeObserver.observe(toolbarNode)
        return () => {
            resizeObserver.disconnect()
        }
    }, [toolbarNode])

    const showMusicDevices = !overflowMusicDevices
    const hideMiddleSection = overflowReactions && !showMusicDevices && overflowRecording

    function renderOverflowButton() {
        if (!showOverflowButton) {
            return null
        }

        return (
            <OverflowMenuButton
                screenShare={overflowScreenshare}
                reactions={overflowReactions}
                musicDevices={overflowMusicDevices && FeatureFlags.isEnabled('desktop-app-only-feature')}
                recording={overflowRecording}
                invite={overflowInvite}
                video={overflowVideo}
                chatController={chatController}
            />
        )
    }
    const overflowButton = renderOverflowButton()
    const isViewer = userState.clientType === 'listenonly'

    const sectionClass = 'flex gap-[6px] md:gap-[16px]'
    return (
        <div id="toolbar-container" className={cn(inSidebar && 'in-sidebar')} ref={toolbarRef}>
            <div
                id="toolbar"
                className={cn(
                    'flex shrink',
                    collapseSectionGap ? 'gap-[6px] md:gap-[16px]' : 'gap-[40px]',
                    userState.clientType !== 'listenonly' ? 'pr-[8px] justify-center' : 'pr-[48px] justify-end'
                )}
            >
                {isViewer ? (
                    <div className={sectionClass}>
                        {inSidebar && <ChatMenuButton chatController={chatController} />}
                        <ReactionsMenuButton />
                        <LeaveSessionMenuButton />
                    </div>
                ) : (
                    <>
                        <div className={sectionClass}>
                            <UserAudioMenuButton noMenu={!env.IS_ELECTRON && inSidebar} />
                            {!overflowVideo && <UserVideoMenuButton noMenu={!env.IS_ELECTRON && inSidebar} />}
                            {inSidebar && <ChatMenuButton chatController={chatController} />}
                            {!overflowScreenshare && <ShareScreenMenuButton />}
                        </div>
                        {!hideMiddleSection && (
                            <div className={sectionClass}>
                                {showMusicDevices && <MusicDevicesMenuButton />}
                                {!overflowRecording && <RecordingMenuButton />}
                                {!overflowReactions && <ReactionsMenuButton />}
                            </div>
                        )}
                        <div className={sectionClass}>
                            {!overflowInvite && <CollaboratorsMenuButton />}
                            {overflowButton}
                            <LeaveSessionMenuButton />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
